import { useEffect, useState } from 'react';
import { formatDate, formatTime } from '@brimo/shared';
import { Banner, Button, Dialog, Skeleton } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { useTask, type QuickPick, type TaskItem } from './api';
import { longDay } from './dates';
import { SnoozeForm, type SnoozeValue } from './SnoozePopover';
import styles from './TaskDetailSheet.module.css';

export interface TaskDetailSheetProps {
  taskId: string | null;
  quickPicks: ReadonlyArray<QuickPick>;
  onClose: () => void;
  /** The row's CTA when it leads to a record (null for tasks without one). */
  onOpen: ((task: TaskItem) => void) | null;
  onComplete: (task: TaskItem) => void;
  onSnooze: (task: TaskItem, value: SnoozeValue) => Promise<void>;
  completing: boolean;
}

/**
 * Detail of a task that has no record to open („ohne Bezug · selbst angelegt") and the mobile detail of
 * every row (Responsive Regeln 02: actions live in the detail): fields, skip history, attachments, and
 * the actions „Später" (inline form) and „Abgeschlossen".
 */
export function TaskDetailSheet({
  taskId,
  quickPicks,
  onClose,
  onOpen,
  onComplete,
  onSnooze,
  completing,
}: TaskDetailSheetProps) {
  const detail = useTask(taskId);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozePending, setSnoozePending] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  useEffect(() => {
    setSnoozing(false);
    setSnoozeError(null);
  }, [taskId]);

  const task = detail.data;
  const open = taskId !== null;
  const priorityLetter = task ? task.priority.toUpperCase() : '';

  async function submitSnooze(value: SnoozeValue) {
    if (!task) return;
    setSnoozePending(true);
    setSnoozeError(null);
    try {
      await onSnooze(task, value);
      setSnoozing(false);
    } catch (error) {
      setSnoozeError(errorMessage(error));
    } finally {
      setSnoozePending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={task ? `${task.typeLabel} · Priorität ${priorityLetter}` : 'Task'}
      title={task ? task.title : 'Task'}
      subtitle={task?.reference}
      footer={
        task && task.status === 'open' ? (
          <>
            <Button variant="secondary" onClick={() => setSnoozing((value) => !value)} disabled={completing}>
              Später
            </Button>
            {onOpen ? (
              <Button variant="secondary" onClick={() => onOpen(task)}>
                {task.cta}
              </Button>
            ) : null}
            <Button onClick={() => onComplete(task)} loading={completing}>
              Abgeschlossen
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        )
      }
    >
      {detail.isError ? <Banner tone="danger">{errorMessage(detail.error)}</Banner> : null}
      {detail.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="60%" />
          <Skeleton width="40%" />
          <Skeleton width="70%" />
        </div>
      ) : null}
      {task ? (
        <div className={styles.body}>
          <dl className={styles.rows}>
            <div className={styles.row}>
              <dt>Fällig</dt>
              <dd>
                {task.due.label}
                {task.due.on ? ` · ${longDay(task.due.on)}` : ''}
              </dd>
            </div>
            <div className={styles.row}>
              <dt>Zuständig</dt>
              <dd>{task.assignee.name}</dd>
            </div>
            <div className={styles.row}>
              <dt>Angelegt</dt>
              <dd>
                {formatDate(task.createdAt)} · {formatTime(task.createdAt)}
                {task.createdBy ? ` · ${task.createdBy.name}` : ''}
              </dd>
            </div>
            {task.stuck ? (
              <div className={styles.row}>
                <dt>Hängt fest</dt>
                <dd>{task.stuck.label}</dd>
              </div>
            ) : null}
            {task.status !== 'open' ? (
              <div className={styles.row}>
                <dt>Status</dt>
                <dd>
                  {task.status === 'done' ? 'erledigt' : 'geschlossen'}
                  {task.outcome?.at ? ` · ${formatDate(task.outcome.at)}` : ''}
                </dd>
              </div>
            ) : null}
          </dl>
          {task.description ? <p className={styles.description}>{task.description}</p> : null}
          {task.skips.length > 0 ? (
            <div>
              <div className={styles.sectionLabel}>Übersprungen</div>
              <ul className={styles.list}>
                {task.skips.map((skip) => (
                  <li key={skip.at} className={styles.listItem}>
                    {skip.label}
                    {skip.note ? <span className={styles.listNote}>„{skip.note}“</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {task.attachments.length > 0 ? (
            <div>
              <div className={styles.sectionLabel}>Anhänge</div>
              <ul className={styles.list}>
                {task.attachments.map((file) => (
                  <li key={file.id} className={styles.listItem}>
                    <a href={`/api/files/${file.fileId}`} target="_blank" rel="noreferrer">
                      {file.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {snoozing ? (
            <div className={styles.snooze}>
              <SnoozeForm
                quickPicks={quickPicks}
                initialOn={task.due.on}
                initialTime={task.due.time}
                pending={snoozePending}
                error={snoozeError}
                onCancel={() => setSnoozing(false)}
                onSubmit={(value) => void submitSnooze(value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
